import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import QuestionPicker from "../../components/QuestionPicker";
import { toast } from "sonner";
import { Loader2, Plus, X, Copy, Send, Archive, FileStack, Info } from "lucide-react";

/**
 * Worksheets: the master copies.
 *
 * A worksheet is built once and kept. It is never given to a student directly
 * - issuing it to a group takes a snapshot of the questions, so editing a
 * worksheet afterwards can never rewrite homework a child has already started.
 * That separation is why "give last year's Week 6 to this year's Year 10" is
 * one click rather than a rebuild.
 */

function Editor({ sheet, onClose, onSaved }) {
  const isNew = !sheet;
  const [title, setTitle] = useState(sheet?.title || "");
  const [kind, setKind] = useState(sheet?.kind || "daily");
  const [notes, setNotes] = useState(sheet?.notes || "");
  const [chosen, setChosen] = useState(sheet?.question_codes || []);
  const [saving, setSaving] = useState(false);
  const [repeats, setRepeats] = useState({});
  const [minutes, setMinutes] = useState(
    sheet?.time_limit_seconds ? String(Math.round(sheet.time_limit_seconds / 60)) : ""
  );

  // Monthly tests and mocks are sat against a clock; daily and weekly work is
  // done at the child's own pace. Only a default - a limit typed on anything
  // is honoured - but it is the right default to land on.
  const timedKind = kind === "monthly" || kind === "mock";

  const toggle = (c) => setChosen((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c]));

  // The chosen basket, not the page on screen. Debounced, because this fires
  // on every tick of a checkbox.
  useEffect(() => {
    if (chosen.length === 0) { setRepeats({}); return undefined; }
    const t = setTimeout(() => {
      api.get("/admin/question-history", { params: { codes: chosen.join(",") } })
        .then((r) => setRepeats(r.data || {}))
        .catch(() => setRepeats({}));
    }, 400);
    return () => clearTimeout(t);
  }, [chosen]);

  // This worksheet is one of the places a chosen question already appears, so
  // it would otherwise report every question in a saved worksheet as a
  // repeat of itself.
  const seenBefore = chosen.filter((c) => (repeats[c]?.given || 0) > 0);

  const save = () => {
    if (!title.trim()) return toast.error("Give it a name");
    setSaving(true);
    const body = {
      title, question_codes: chosen, kind, notes,
      // 0 rather than null clears a limit that was there before.
      time_limit_minutes: minutes.trim() ? Number(minutes) : 0,
    };
    const req = isNew ? api.post("/admin/sets", body) : api.patch(`/admin/sets/${sheet.id}`, body);
    req
      .then((r) => {
        if (!isNew && r.data.already_issued > 0) {
          toast.success(`Saved. ${r.data.already_issued} issue(s) already out are unchanged.`);
        } else {
          toast.success(isNew ? "Worksheet created" : "Saved");
        }
        onSaved(); onClose();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl my-6 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">{isNew ? "New worksheet" : `Edit — ${sheet.title}`}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {!isNew && (
            <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5">
              <Info size={15} className="mt-0.5 shrink-0 text-blue-600" />
              <p className="text-sm text-blue-900">
                Editing this changes what <strong>future</strong> students get. Work already given out keeps the
                questions it was issued with.
              </p>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Name</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Week 6 — Fractions and Equations"
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Type</span>
              <select
                value={kind}
                onChange={(e) => {
                  const next = e.target.value;
                  setKind(next);
                  // Offer a sensible clock when switching to a timed type, and
                  // take it away when switching to a self-paced one, rather
                  // than leaving 60 minutes sitting on a daily worksheet.
                  if (next === "monthly" || next === "mock") {
                    if (!minutes.trim()) setMinutes(next === "mock" ? "90" : "45");
                  } else {
                    setMinutes("");
                  }
                }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2">
                <option value="daily">Daily worksheet</option>
                <option value="weekly">Weekly mini test</option>
                <option value="monthly">Monthly test</option>
                <option value="mock">Mock exam</option>
                <option value="practice">Practice</option>
                <option value="homework">Homework (old)</option>
                <option value="test">Test (old)</option>
              </select>
            </label>
          </div>

          <label className="text-sm block max-w-xs">
            <span className="block text-slate-600 mb-1">
              Time limit {timedKind ? "" : "(optional)"}
            </span>
            <div className="flex items-center gap-2">
              <input type="number" min="1" value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder={timedKind ? "45" : "no limit"}
                className="w-28 border border-slate-300 rounded-lg px-3 py-2" />
              <span className="text-sm text-slate-500">minutes</span>
            </div>
            <span className="block text-xs text-slate-500 mt-1">
              {minutes.trim()
                ? "One sitting. The clock starts when the child opens it, and they get five minutes afterwards to photograph their working."
                : "No clock — they work at their own pace and can come back to it."}
            </span>
          </label>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Notes to yourself (optional)</span>
            <input value={notes || ""} onChange={(e) => setNotes(e.target.value)}
              placeholder="Covers week 6 lesson — ratio and inequalities"
              className="w-full border border-slate-300 rounded-lg px-3 py-2" />
          </label>

          <div>
            <p className="text-sm text-slate-600 mb-2">Questions — <strong>{chosen.length} chosen</strong></p>
            {chosen.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {chosen.map((c) => {
                  const times = repeats[c]?.given || 0;
                  return (
                    <button key={c} type="button" onClick={() => toggle(c)}
                      title={times > 0 ? `Given ${times} time(s) before` : "Never given before"}
                      className={`font-mono text-xs rounded-full pl-2.5 pr-1.5 py-1 flex items-center gap-1 ${
                        times > 0
                          ? "bg-amber-100 text-amber-900 border border-amber-300"
                          : "bg-slate-800 text-white"}`}>
                      {c}
                      {times > 0 && <span className="font-sans font-semibold">{times}&times;</span>}
                      <X size={11} />
                    </button>
                  );
                })}
              </div>
            )}

            {seenBefore.length > 0 && (
              <p className="mb-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <strong>{seenBefore.length} of these {chosen.length} questions
                {seenBefore.length === 1 ? " has" : " have"} been given before.</strong>{" "}
                They are marked in amber above, and the list below says where. Setting one again
                is fine — this is a note, not a rule.
              </p>
            )}
            <QuestionPicker chosen={chosen} onToggle={toggle} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create worksheet" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function IssueModal({ sheet, groups, students, onClose, onIssued }) {
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const [extra, setExtra] = useState([]);
  const [due, setDue] = useState("");
  const [marking, setMarking] = useState("self");
  const [saving, setSaving] = useState(false);
  const sheetMinutes = sheet.time_limit_seconds
    ? String(Math.round(sheet.time_limit_seconds / 60)) : "";
  const [minutes, setMinutes] = useState(sheetMinutes);

  const go = () => {
    if (!groupId && extra.length === 0) return toast.error("Pick a group or some students");
    setSaving(true);
    api.post("/admin/issues", {
      set_id: sheet.id, group_id: groupId || null, student_ids: extra,
      due_date: due || null, marking,
      // Only sent when it differs from the worksheet, so the usual case stays
      // "whatever the paper says" and an override is a deliberate act.
      ...(minutes === sheetMinutes
        ? {}
        : { time_limit_minutes: minutes.trim() ? Number(minutes) : 0 }),
    })
      .then((r) => { toast.success(`Given to ${r.data.student_ids.length} student(s)`); onIssued(); onClose(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not issue it"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-lg my-6 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">Give out — {sheet.title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-500">
            {sheet.question_count ?? sheet.question_codes?.length} questions. A copy of this list is frozen onto
            the assignment, so editing the worksheet later will not change it.
          </p>

          <label className="text-sm block">
            <span className="block text-slate-600 mb-1">Group</span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2">
              <option value="">— none —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name} ({g.student_count})</option>
              ))}
            </select>
          </label>

          <div>
            <p className="text-sm text-slate-600 mb-1.5">Plus anyone else (optional)</p>
            <div className="flex flex-wrap gap-1.5">
              {students.map((s) => (
                <button key={s.id} type="button"
                  onClick={() => setExtra((x) => (x.includes(s.id) ? x.filter((y) => y !== s.id) : [...x, s.id]))}
                  className={`text-sm rounded-full border px-3 py-1.5 ${
                    extra.includes(s.id) ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 hover:bg-slate-50"}`}>
                  {s.name}
                </button>
              ))}
              {students.length === 0 && <p className="text-sm text-slate-400">No student accounts yet.</p>}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Due date</span>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">
                Time limit {minutes !== sheetMinutes && <span className="text-amber-700">· changed</span>}
              </span>
              <div className="flex items-center gap-2">
                <input type="number" min="1" value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  placeholder="no limit"
                  className="w-24 border border-slate-300 rounded-lg px-3 py-2" />
                <span className="text-sm text-slate-500">min</span>
              </div>
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Marking</span>
              <select value={marking} onChange={(e) => setMarking(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2">
                <option value="self">They self-mark</option>
                <option value="tutor">I mark it</option>
              </select>
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={go} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {saving ? "Giving out…" : "Give it out"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminWorksheets() {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);  // undefined = closed, null = new
  const [issuing, setIssuing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/sets").then((r) => setRows(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load worksheets"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/groups").then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
    api.get("/admin/students?role=student")
      .then((r) => setStudents((r.data.users || []).filter((u) => u.role === "student")))
      .catch(() => setStudents([]));
  }, [load]);

  const duplicate = (id) =>
    api.post(`/admin/sets/${id}/duplicate`).then(() => { toast.success("Copied"); load(); })
      .catch(() => toast.error("Could not copy it"));

  const archive = (s) =>
    api.patch(`/admin/sets/${s.id}`, { archived: true })
      .then(() => { toast.success("Archived — nothing is deleted"); load(); })
      .catch(() => toast.error("Could not archive it"));

  return (
    <AdminShell title="Worksheets" subtitle="Master copies you build once and give out again each year">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-500">
          {loading ? "Loading…" : `${rows.length} worksheet${rows.length === 1 ? "" : "s"}`}
        </p>
        <button onClick={() => setEditing(null)}
          className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-semibold rounded-lg px-3.5 py-2">
          <Plus size={15} /> New worksheet
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && rows.length === 0 && (
          <p className="py-14 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Loading…
          </p>
        )}
        {!loading && rows.length === 0 && (
          <div className="py-14 text-center">
            <FileStack size={26} className="mx-auto text-slate-300 mb-3" />
            <p className="font-semibold text-slate-600">No worksheets yet</p>
            <p className="text-sm text-slate-400 mt-1">Build one once, then give it out every year.</p>
          </div>
        )}
        {rows.map((s) => (
          <div key={s.id} className="px-4 py-3.5 border-b border-slate-100 last:border-b-0 flex flex-wrap items-center gap-x-3 gap-y-2 hover:bg-slate-50/60">
            <button onClick={() => setEditing(s)} className="flex-1 min-w-[150px] text-left">
              <span className="block font-medium text-slate-900">{s.title}</span>
              <span className="block text-xs text-slate-500 capitalize">
                {s.kind} · {s.question_count} questions
                {s.times_issued > 0 ? ` · given out ${s.times_issued} time${s.times_issued === 1 ? "" : "s"}` : " · never given out"}
              </span>
            </button>
            <button onClick={() => setIssuing(s)}
              className="text-xs inline-flex items-center gap-1 bg-slate-800 text-white rounded-lg px-2.5 py-1.5 font-semibold">
              <Send size={12} /> Give out
            </button>
            <button onClick={() => setEditing(s)}
              className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-white">Edit</button>
            <button onClick={() => duplicate(s.id)} title="Make a copy"
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 hover:bg-white"><Copy size={12} /></button>
            <button onClick={() => archive(s)} title="Archive (nothing is deleted)"
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 hover:bg-white text-slate-500"><Archive size={12} /></button>
          </div>
        ))}
      </div>

      {editing !== undefined && (
        <Editor sheet={editing} onClose={() => setEditing(undefined)} onSaved={load} />
      )}
      {issuing && (
        <IssueModal sheet={issuing} groups={groups} students={students}
          onClose={() => setIssuing(null)} onIssued={load} />
      )}
    </AdminShell>
  );
}
